import React, { useEffect, useState } from "react";
import { PieChart, Pie, Cell, Legend, Tooltip } from "recharts";
import axios from "../../../utils/Security/axiosInstance";
import config from "../../../config";
import "../../../Style/DashboardPieCharts.css";
const COLORS = [
  "#c5aef7", // light teal
  "#a791e3", // medium teal
  "#7861c6", // darker teal
  "#4832a4", // deep teal
  "#320881", // very dark teal
  "#4e1a92ff", // darker green-teal
  "#196355", // muted green-teal
  "#1e7a68", // mid teal
  "#3a9d88", // repeat lighter teal
  "#61d2c0", // soft pastel teal
];

const TYPES = ["incident", "change", "task", "problem", "rma"] as const;
type EntityType = (typeof TYPES)[number];

interface CustomerEntityCount {
  customerName: string;
  type: EntityType;
  count: number;
}

interface PieDataEntry {
  name: string;
  value: number;
  others?: PieDataEntry[];
  [key: string]: any; // 👈 allow extra props so TS is happy
}
const now = new Date();
const monthYear = now.toLocaleString("default", {
  month: "long",
  year: "numeric",
});

const typeLabels: Record<EntityType, string> = {
  incident: "Incident",
  change: "Changes",
  task: "Tasks",
  problem: "Problems",
  rma: "RMA",
};

const CustomTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: any;
}) => {
  if (active && payload?.length > 0) {
    const data = payload[0].payload;
    if (data.name === "Others" && data.others?.length > 0) {
      return (
        <div className="tooltip-container">
          <strong>Others:</strong>
          <ul className="tooltip-list">
            {data.others.map((entry: PieDataEntry, index: number) => (
              <li key={index}>
                {entry.name}: {entry.value}
              </li>
            ))}
          </ul>
        </div>
      );
    } else {
      return (
        <div className="tooltip-container">
          <strong>{data.name}</strong>: {data.value}
        </div>
      );
    }
  }
  return null;
};

export default function DashboardPieCharts() {
  const [dataByType, setDataByType] = useState<
    Record<EntityType, PieDataEntry[]>
  >(() => {
    const initial: Partial<Record<EntityType, PieDataEntry[]>> = {};
    TYPES.forEach((type) => {
      initial[type] = [];
    });
    return initial as Record<EntityType, PieDataEntry[]>;
  });
  const [darkMode, setDarkMode] = useState<boolean>(
    () => localStorage.getItem("darkMode") === "true"
  );

  useEffect(() => {
    document.body.classList.toggle("dark-mode", darkMode);
    localStorage.setItem("darkMode", String(darkMode));
  }, [darkMode]);

  useEffect(() => {
    const isAdmin = localStorage.getItem("isAdmin") === "true";
    const userId = localStorage.getItem("userid");
    const suffix = !isAdmin && userId ? `?userId=${userId}` : "";

    axios
      .get<CustomerEntityCount[]>(
        `${config.apiBaseUrl}/api/dashboard/customer-entity-counts${suffix}`
      )
      .then((response) => {
        const grouped: Record<EntityType, PieDataEntry[]> = {
          incident: [],
          change: [],
          task: [],
          problem: [],
          rma: [],
        };

        response.data.forEach(({ customerName, type, count }) => {
          if (grouped[type] && customerName && customerName !== "null") {
            grouped[type].push({ name: customerName, value: count });
          }
        });

        Object.entries(grouped).forEach(([type, entries]) => {
          const sorted = entries.sort((a, b) => b.value - a.value);
          const top = sorted.slice(0, 5);
          const others = sorted.slice(5);
          const othersTotal = others.reduce(
            (sum, entry) => sum + entry.value,
            0
          );

          if (others.length > 0) {
            top.push({ name: "Others", value: othersTotal, others });
          }

          grouped[type as EntityType] = top;
        });

        setDataByType(grouped);
      })
      .catch(console.error);
  }, []);

  return (
    <div className="pie-diagram">
      <h2 className="diagram-title">
        Customer Distribution - {monthYear}
      </h2>
      <div className="card-container">
        {TYPES.map((type) => (
          <div className="card" key={type}>
            <h3 className="card-title">{typeLabels[type]}</h3>
            {dataByType[type].length > 0 ? (
              <PieChart width={300} height={300}>
                <Pie
                  data={dataByType[type]}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  isAnimationActive={false}
                >
                  {dataByType[type].map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  wrapperStyle={{ color: "var(--text-color)" }}
                />
              </PieChart>
            ) : (
              <div className="no-data-text">No data available</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
